import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';

const CourseContext = createContext(null);

const apiUrl = process.env.REACT_APP_API_URL;

// A seleção explícita é uma preferência deste navegador, separada por conta.
// O backend continua sendo o fallback em um dispositivo novo ou sem seleção válida.
function selectionKey() {
  try {
    const userId = localStorage.getItem('userId');
    return userId ? `preparate.activeCourse.${userId}` : null;
  } catch { return null; }
}
function readSelection(key) {
  try { return key ? localStorage.getItem(key) || '' : ''; } catch { return ''; }
}
function saveSelection(key, id) {
  try {
    if (key) {
      if (id) localStorage.setItem(key, id);
      else localStorage.removeItem(key);
    }
  } catch { /* A navegação continua funcionando se o armazenamento estiver bloqueado. */ }
}
export const CourseProvider = ({ children }) => {
  const [courses, setCourses] = useState([]);
  const [activeCourseId, setActiveCourseIdState] = useState('');
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [hasFreePlan, setHasFreePlan] = useState(false);
  const selection = useRef({ key: selectionKey(), id: '' });

  const setActiveCourseId = useCallback((courseId) => {
    const id = courseId ? String(courseId) : '';
    const key = selectionKey();
    selection.current = { key, id };
    saveSelection(key, id);
    setActiveCourseIdState(id);
  }, []);

  const fetchCourses = useCallback(async () => {
    const key = selectionKey();
    // Sessão vem de um cookie httpOnly (enviado automaticamente pelo axios,
    // ver src/index.js) — o front não tem mais como checar "está logado?"
    // antes de disparar a chamada. Sem sessão válida, o backend responde 403
    // e a lista some vazia, tratado abaixo sem logar erro (é o estado
    // esperado ao carregar a tela de login, por exemplo).
    try {
      // GET /cursosDisponiveis lista todos os concursos globais + os
      // personalizados do próprio usuário (isCustom: true), sem filtrar por
      // plano — quem já é "registrado" (isCustom || hasPlan) é decidido no
      // front (registeredCourses abaixo), pra gatear "adicionar concurso" no
      // plano grátis (ver CourseSwitcher).
      const [coursesResponse, activeCourseResponse] = await Promise.all([
        axios.get(apiUrl + 'cursosDisponiveis'),
        axios.get(apiUrl + 'studyPlan/activeCourse')
      ]);
      if (key !== selectionKey()) return;
      const available = coursesResponse.data.courses || [];
      const isValid = id => !!id && available.some(course => String(course.id) === String(id));
      const saved = readSelection(key);
      const current = selection.current.key === key ? selection.current.id : '';
      if (saved && !isValid(saved)) saveSelection(key, '');
      const preferred = isValid(current) ? current : isValid(saved) ? saved : '';
      const fallback = activeCourseResponse.data.courseId;
      const resolved = preferred || (isValid(fallback) ? String(fallback) : '');
      selection.current = { key, id: resolved };
      setCourses(available);
      setActiveCourseIdState(resolved);
    } catch (error) {
      if (error.response?.status !== 403) {
        console.error('Erro ao buscar concursos do usuário:', error);
      } else {
        selection.current = { key, id: '' };
        setCourses([]);
        setActiveCourseIdState('');
        setHasFreePlan(false);
      }
      return;
    } finally {
      setLoadingCourses(false);
    }

    // Chamada à parte (não bloqueia a lista de concursos se falhar) — só
    // usada pro gate de "1 concurso no plano grátis" em CourseSwitcher.
    try {
      const { data } = await axios.get(apiUrl + 'plans');
      setHasFreePlan(!!data.hasFreePlan);
    } catch (error) {
      if (error.response?.status !== 403) {
        console.error('Erro ao buscar status do plano:', error);
      }
    }
  }, []);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  const createCustomCourse = useCallback(async (name, description, examDate) => {
    const response = await axios.post(apiUrl + 'myCourses', { name, description, exam_date: examDate });
    const newCourseId = response.data.id;
    await fetchCourses();
    setActiveCourseId(newCourseId);
    return newCourseId;
  }, [fetchCourses, setActiveCourseId]);

  const updateCustomCourse = useCallback(async (courseId, name, description, imageUrl, examDate) => {
    await axios.put(apiUrl + `myCourses/${courseId}`, { name, description, image_url: imageUrl, exam_date: examDate });
    // Atualiza os dados exibidos sem recarregar as demais informações da conta.
    const { data } = await axios.get(apiUrl + 'cursosDisponiveis');
    setCourses(data.courses || []);
  }, []);

  const deleteCustomCourse = useCallback(async (courseId) => {
    await axios.delete(apiUrl + `myCourses/${courseId}`);
    await fetchCourses();
  }, [fetchCourses]);

  const resetCourseProgress = useCallback(async (courseId) => {
    await axios.post(apiUrl + `myCourses/${courseId}/reset-progress`);
    await fetchCourses();
  }, [fetchCourses]);

  // Se o curso ativo (escolhido nessa sessão) não existir mais na lista
  // (ex.: era um concurso personalizado apagado), limpa a seleção.
  useEffect(() => {
    if (loadingCourses || !activeCourseId) return;
    const stillValid = courses.some((course) => String(course.id) === activeCourseId);
    if (!stillValid) {
      setActiveCourseId('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courses, loadingCourses]);

  const activeCourse = courses.find((course) => String(course.id) === activeCourseId) || null;

  // Concursos que o usuário já "tem" de fato: os personalizados (sempre dele)
  // e os globais pros quais ele já gerou um plano de estudos (hasPlan, ver
  // getAvailableCoursesForUser no backend). Usado pelo dropdown da home
  // (CourseSwitcher), que não deve listar o catálogo inteiro — só
  // /escolher-concurso (CourseSelection) mostra tudo, pra adicionar um novo.
  const registeredCourses = useMemo(
    () => courses.filter((course) => course.isCustom || course.hasPlan),
    [courses]
  );

  return (
    <CourseContext.Provider
      value={{
        courses,
        setCourses,
        registeredCourses,
        activeCourseId,
        setActiveCourseId,
        loadingCourses,
        hasFreePlan,
        activeCourse,
        createCustomCourse,
        updateCustomCourse,
        deleteCustomCourse,
        resetCourseProgress,
        refreshCourses: fetchCourses
      }}
    >
      {children}
    </CourseContext.Provider>
  );
};

export const useCourse = () => useContext(CourseContext);
